import { useEffect, useRef, useState } from "react";
import { BellRing, ChevronDown, Download, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import CircularProgress from "@/components/common/CircularProgress";
import { comunidadAdapter, ComunidadFormacionOption, FormacionAdminSummary } from "@/hooks/api/comunidad";
import { toastUtils } from "@/hooks/useToast";
import { MeetAttendanceRow, parseMeetAttendanceCsv } from "../meetAttendance";
import { RATING_LABELS } from "./FormacionRatingCard";
import { FormacionRecordatorioPanel } from "./FormacionRecordatorioPanel";
import { FormacionDevolucionPanel } from "./FormacionDevolucionPanel";

const ATTENDANCE_THRESHOLD = 0.75;

const editionLabel = (edition: number) => `${edition}ª edición`;

/** Una calificación es de una edición puntual: se cruza por edición + mail, no solo por mail. */
const ratingKey = (edition: number, email: string) => `${edition}|${email.toLowerCase()}`;

/** CSV con BOM y `;` — Excel en castellano lo abre directo en columnas, con tildes bien. */
function downloadInscriptosCsv(summary: FormacionAdminSummary) {
  const esc = (v: string) => `"${v.replace(/"/g, '""')}"`;
  const header = [
    "Nombre",
    "Apellido",
    "Correo electrónico",
    "Edición",
    "Fecha de inscripción",
    "Minutos en el Meet",
    "Participó (≥75%)",
    "Calificación (1 a 5)",
    "Comentario",
  ];
  const ratingByEditionEmail = new Map(
    summary.calificaciones.items.map((c) => [ratingKey(c.edition, c.email), c]),
  );
  const lines = summary.inscriptos.map((i) => {
    const rating = ratingByEditionEmail.get(ratingKey(i.edition, i.email));
    return [
      i.name,
      i.surname,
      i.email,
      i.edition === 1
        ? "Primera"
        : i.edition === 2
          ? "Segunda"
          : editionLabel(i.edition),
      new Date(i.createdAt).toLocaleString("es-AR"),
      i.minutesAttended === null ? "" : String(i.minutesAttended),
      i.attended === null ? "Sin informe todavía" : i.attended ? "Sí" : "No",
      rating ? String(rating.rating) : "",
      rating?.comment ?? "",
    ]
      .map(esc)
      .join(";");
  });
  const csv = "\uFEFF" + [header.map(esc).join(";"), ...lines].join("\r\n");
  const url = URL.createObjectURL(
    new Blob([csv], { type: "text/csv;charset=utf-8" }),
  );
  const a = document.createElement("a");
  a.href = url;
  a.download = `inscriptos-${summary.key}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Panel de admin: inscriptos a la formación del mes (se registran al tocar "Anotarme") y carga
 * del informe de asistencia de Google Meet por edición. Quien se quedó ≥75% del encuentro
 * pasa a ver "Calificá" y "Compartí tu logro" en Comunidad.
 */
export const FormacionInscriptosPanel = () => {
  const [summary, setSummary] = useState<FormacionAdminSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState<{
    edition: number;
    rows: MeetAttendanceRow[];
  } | null>(null);
  // Cuánto duró DE VERDAD la edición: se confirma al subir el informe (arranca con lo cargado
  // en el calendario). Es la base del 75% y la duración que figura en el certificado.
  const [realDuration, setRealDuration] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingEditionRef = useRef<number | null>(null);
  // Las formaciones salen del calendario del panel; por defecto se muestra la más cercana a hoy.
  const [options, setOptions] = useState<ComunidadFormacionOption[]>([]);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  // "Avisar antes de esta edición": edición cuyo recordatorio se está revisando.
  const [recordatorioEdition, setRecordatorioEdition] = useState<number | null>(null);
  // Calificaciones: arranca cerrada (el resumen se ve igual en el encabezado).
  const [ratingsOpen, setRatingsOpen] = useState(false);

  useEffect(() => {
    comunidadAdapter
      .listFormacionOptions()
      .then((opts) => {
        setOptions(opts);
        const now = Date.now();
        const closest = [...opts].sort(
          (a, b) =>
            Math.abs(new Date(a.firstStartsAt ?? 0).getTime() - now) -
            Math.abs(new Date(b.firstStartsAt ?? 0).getTime() - now),
        )[0];
        setSelectedKey(closest?.key ?? null);
        if (!closest) setLoading(false);
      })
      .catch(() => {
        toastUtils.error("No se pudo cargar la lista de formaciones.");
        setLoading(false);
      });
  }, []);

  const reload = async (key = selectedKey) => {
    if (!key) return;
    setLoading(true);
    try {
      setSummary(await comunidadAdapter.getFormacionAdmin(key));
    } catch {
      setSummary(null);
      toastUtils.error("No se pudo cargar esta formación. Si la acabás de cargar en el calendario, tocá «Publicar cambios».");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setRecordatorioEdition(null);
    reload(selectedKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKey]);

  const pickFile = (edition: number) => {
    pendingEditionRef.current = edition;
    fileInputRef.current?.click();
  };

  const handleFile = async (file: File | undefined) => {
    const edition = pendingEditionRef.current;
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (!file || edition === null) return;
    try {
      const rows = parseMeetAttendanceCsv(await file.text());
      if (rows.length === 0) {
        toastUtils.error("El archivo no tiene filas con mail.");
        return;
      }
      setPreview({ edition, rows });
      setRealDuration(
        String(summary?.editions.find((e) => e.edition === edition)?.durationMinutes ?? ""),
      );
    } catch (e) {
      toastUtils.error(
        e instanceof Error ? e.message : "No se pudo leer el archivo.",
      );
    }
  };

  // "Avisar que el certificado está listo": para una edición cuya asistencia ya se cargó (por
  // ejemplo, antes de que existiera el aviso automático). No toca la asistencia; solo avisa a
  // quienes llegaron al 75% y todavía no recibieron el aviso.
  const [sendingAvisos, setSendingAvisos] = useState(false);
  const handleAvisos = async () => {
    if (!selectedKey) return;
    setSendingAvisos(true);
    try {
      const pendientes = await comunidadAdapter.countAvisosCertificado(selectedKey);
      if (pendientes === 0) {
        toastUtils.info(
          "No hay a quién avisar: ya recibieron el aviso, o todavía no se cargó la asistencia de Meet.",
        );
        return;
      }
      const ok = window.confirm(
        `Se le va a avisar a ${pendientes} ${pendientes === 1 ? "psicóloga" : "psicólogas"} que ya tienen su certificado (campanita y celular). La asistencia no cambia. ¿Enviar?`,
      );
      if (!ok) return;
      const avisados = await comunidadAdapter.sendAvisosCertificado(selectedKey);
      toastUtils.success(`Listo: les avisamos a ${avisados} que ya tienen su certificado.`);
    } catch {
      toastUtils.error("No se pudo mandar el aviso. Probá de nuevo.");
    } finally {
      setSendingAvisos(false);
    }
  };

  const confirmUpload = async () => {
    if (!preview) return;
    const minutes = Number(realDuration);
    if (!Number.isInteger(minutes) || minutes < 1 || minutes > 600) {
      toastUtils.error("Escribí cuántos minutos duró la edición (un número entre 1 y 600).");
      return;
    }
    setUploading(true);
    try {
      const { participantes, total, avisados } =
        await comunidadAdapter.uploadFormacionAsistencia(
          selectedKey as string,
          preview.edition,
          // Los mails tapados por Meet no se pueden cruzar con la plataforma: no se cargan.
          preview.rows.filter((r) => !r.masked).map(({ email, minutes }) => ({ email, minutes })),
          minutes,
        );
      toastUtils.success(
        `Asistencia cargada: ${participantes} de ${total} participaron (≥75%).` +
          (avisados > 0
            ? ` Les avisamos a ${avisados} que ya tienen su certificado.`
            : ""),
      );
      setPreview(null);
      reload();
    } catch {
      toastUtils.error("No se pudo cargar la asistencia. Probá de nuevo.");
    } finally {
      setUploading(false);
    }
  };

  const ratingByEditionEmail = new Map(
    (summary?.calificaciones.items ?? []).map((c) => [
      ratingKey(c.edition, c.email),
      c,
    ]),
  );
  const previewDuration = Number(realDuration) || 0;
  const previewMin = previewDuration * ATTENDANCE_THRESHOLD;

  return (
    <div className="flex flex-col gap-4 rounded-[20px] bg-white p-6 shadow-[0_6px_20px_rgba(109,87,188,0.08)]">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="m-0 text-base font-bold text-[#2D2049]">
            Inscriptos a la formación
          </h3>
          {options.length > 0 ? (
            <select
              id="formacion-inscriptos-select"
              className="mt-1 max-w-full rounded-lg border border-[#C9BCE8] bg-white px-2 py-1 text-sm text-[#2D2049]"
              value={selectedKey ?? ""}
              onChange={(e) => setSelectedKey(e.target.value)}
            >
              {options.map((o) => (
                <option key={o.key} value={o.key}>
                  {o.title}
                  {o.firstStartsAt ? ` — ${new Date(o.firstStartsAt).toLocaleDateString("es-AR", { month: "long", year: "numeric" })}` : ""}
                </option>
              ))}
            </select>
          ) : (
            <p className="m-0 mt-1 text-sm text-gray-500">Todavía no hay formaciones cargadas en el calendario.</p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            className="border-[#C9BCE8] text-[#665090]"
            disabled={!summary || sendingAvisos}
            onClick={handleAvisos}
          >
            <BellRing size={15} className="mr-1.5" />
            {sendingAvisos ? "Enviando…" : "Avisar que el certificado está listo"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="border-[#C9BCE8] text-[#665090]"
            disabled={!summary || summary.inscriptos.length === 0}
            onClick={() => summary && downloadInscriptosCsv(summary)}
          >
            <Download size={15} className="mr-1.5" />
            Descargar Excel
          </Button>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,text/csv"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />

      {loading ? (
        <div className="flex justify-center py-6">
          <CircularProgress />
        </div>
      ) : summary ? (
        <>
          <div className="grid grid-cols-2 gap-4">
            {summary.editions.map((ed) => (
              <div
                key={ed.edition}
                className="flex flex-col gap-2 rounded-xl border border-[#E3D9FF] p-4"
              >
                <span className="text-xs font-bold uppercase tracking-wide text-[#8475B3]">
                  {editionLabel(ed.edition)}
                </span>
                <span className="text-2xl font-extrabold text-[#2D2049]">
                  {ed.inscriptos}{" "}
                  <span className="text-sm font-semibold text-gray-500">
                    {ed.inscriptos === 1 ? "inscripta/o" : "inscriptas/os"}
                  </span>
                </span>
                <span className="text-[13px] text-gray-500">
                  {ed.participantes === null
                    ? "Todavía no se subió la asistencia de Meet"
                    : `${ed.participantes} ${ed.participantes === 1 ? "participó" : "participaron"} (≥75% de ${ed.durationMinutes} min, duración confirmada)`}
                </span>
                {ed.asistentesSinInscripcion.length > 0 && (
                  <span className="text-xs text-amber-700">
                    {ed.asistentesSinInscripcion.length} en el Meet sin
                    inscripción con ese mail:{" "}
                    {ed.asistentesSinInscripcion.join(", ")}
                  </span>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  className="self-start border-[#C9BCE8] text-[#665090]"
                  onClick={() => pickFile(ed.edition)}
                >
                  <Upload size={15} className="mr-1.5" />
                  {ed.participantes === null
                    ? "Subir asistencia de Meet"
                    : "Reemplazar asistencia"}
                </Button>
                {/* Antes de que pase (todavía sin asistencia cargada): recordatorio por grupos. */}
                {ed.participantes === null && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="self-start border-[#C9BCE8] text-[#665090]"
                    onClick={() => setRecordatorioEdition(ed.edition)}
                  >
                    <BellRing size={15} className="mr-1.5" />
                    Avisar antes de esta edición
                  </Button>
                )}
              </div>
            ))}
          </div>

          <p className="m-0 text-xs leading-relaxed text-gray-400">
            Para la asistencia: abrí el informe que Meet le manda a quien
            organiza, y descargalo como CSV (Archivo → Descargar → Valores
            separados por comas). Se cruza por mail con la plataforma.
          </p>

          {recordatorioEdition !== null && selectedKey && (
            <FormacionRecordatorioPanel
              formacionKey={selectedKey}
              edition={recordatorioEdition}
              onClose={() => setRecordatorioEdition(null)}
            />
          )}

          {preview && (
            <div className="flex flex-col gap-3 rounded-xl border border-[#C9BCE8] bg-[#F5F1FF] p-4">
              <span className="text-sm font-bold text-[#2D2049]">
                Revisá antes de cargar — {editionLabel(preview.edition)}:{" "}
                {preview.rows.filter((r) => !r.masked && r.minutes >= previewMin).length} de{" "}
                {preview.rows.filter((r) => !r.masked).length} llegan al 75%
              </span>
              {preview.rows.some((r) => r.masked) && (
                <span className="text-xs text-amber-700">
                  {preview.rows.filter((r) => r.masked).length} con el mail oculto por Meet (entraron con una cuenta de
                  afuera): no se pueden cruzar con la plataforma, así que no se cargan. Si alguna es de una psicóloga,
                  pedile que la próxima vez entre con el mail con el que usa la plataforma.
                </span>
              )}
              {/* Antes de cargar: cuánto duró de verdad. Sale en el certificado. */}
              <label className="flex flex-wrap items-center gap-2 text-[13px] text-[#2D2049]">
                <span className="font-semibold">¿Cuánto duró realmente esta edición?</span>
                <input
                  id="formacion-duracion-real"
                  type="number"
                  min={1}
                  max={600}
                  className="w-20 rounded-lg border border-[#C9BCE8] bg-white px-2 py-1 text-sm"
                  value={realDuration}
                  onChange={(e) => setRealDuration(e.target.value)}
                />
                <span>minutos</span>
                <span className="w-full text-xs text-gray-500">
                  Chequealo con quien dio la formación: esta duración es la que figura en el certificado y la base del 75%.
                </span>
              </label>
              <div className="max-h-[260px] overflow-y-auto rounded-lg bg-white">
                <table className="w-full text-left text-[13px]">
                  <thead className="sticky top-0 bg-white text-xs text-gray-500">
                    <tr>
                      <th className="px-3 py-2">Mail</th>
                      <th className="px-3 py-2">Nombre</th>
                      <th className="px-3 py-2">Minutos</th>
                      <th className="px-3 py-2">Participó</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((r) => (
                      <tr key={r.masked ? `masked:${r.name}:${r.email}` : r.email} className="border-t border-[#F0EBFF]">
                        <td className="px-3 py-1.5">{r.email}</td>
                        <td className="px-3 py-1.5">{r.name}</td>
                        <td className="px-3 py-1.5">{r.minutes}</td>
                        <td className="px-3 py-1.5">
                          {r.masked ? (
                            <span className="text-amber-700">Mail oculto — no se carga</span>
                          ) : r.minutes >= previewMin ? (
                            "Sí"
                          ) : (
                            "No"
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPreview(null)}
                  disabled={uploading}
                >
                  Cancelar
                </Button>
                <Button
                  size="sm"
                  className="bg-[#665090] hover:bg-[#665090]/90"
                  onClick={confirmUpload}
                  disabled={uploading}
                >
                  {uploading ? "Cargando…" : "Confirmar y cargar"}
                </Button>
              </div>
            </div>
          )}

          {summary.inscriptos.length === 0 ? (
            <p className="m-0 text-sm text-gray-400">
              Todavía no se anotó nadie.
            </p>
          ) : (
            <div className="max-h-[360px] overflow-y-auto rounded-xl border border-[#E3D9FF]">
              <table className="w-full text-left text-[13px]">
                <thead className="sticky top-0 bg-[#F5F1FF] text-xs text-gray-500">
                  <tr>
                    <th className="px-3 py-2">Nombre</th>
                    <th className="px-3 py-2">Mail</th>
                    <th className="px-3 py-2">Edición</th>
                    <th className="px-3 py-2">Asistencia</th>
                    <th className="px-3 py-2">Calificación</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.inscriptos.map((i) => {
                    const rating = ratingByEditionEmail.get(ratingKey(i.edition, i.email));
                    return (
                      <tr
                        key={`${i.edition}-${i.email}`}
                        className="border-t border-[#F0EBFF]"
                      >
                        <td className="px-3 py-1.5">
                          {`${i.name} ${i.surname}`.trim() || "—"}
                        </td>
                        <td className="px-3 py-1.5">{i.email}</td>
                        <td className="px-3 py-1.5">
                          {editionLabel(i.edition)}
                        </td>
                        <td className="px-3 py-1.5">
                          {i.attended === null
                            ? "—"
                            : i.attended
                              ? `Participó (${i.minutesAttended} min)`
                              : `No llegó al 75% (${i.minutesAttended} min)`}
                        </td>
                        <td className="px-3 py-1.5">
                          {rating ? (
                            <>
                              <span className="font-semibold text-[#2D2049]">
                                {rating.rating}/5
                              </span>
                              {rating.comment && (
                                <p className="m-0 mt-0.5 whitespace-pre-line text-gray-500">
                                  {rating.comment}
                                </p>
                              )}
                            </>
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div className="flex flex-col gap-2 rounded-xl border border-[#E3D9FF] p-4">
            <button
              type="button"
              onClick={() => setRatingsOpen((o) => !o)}
              aria-expanded={ratingsOpen}
              className="flex w-full flex-wrap items-center gap-2 text-left"
            >
              <span className="text-xs font-bold uppercase tracking-wide text-[#8475B3]">Calificaciones</span>
              <span className="text-[13px] text-gray-500">
                {summary.calificaciones.count === 0
                  ? "· todavía nadie calificó"
                  : `· ${summary.calificaciones.average?.toLocaleString("es-AR")} de 5 · ${summary.calificaciones.count} ${
                      summary.calificaciones.count === 1 ? "calificación" : "calificaciones"
                    }`}
              </span>
              <ChevronDown
                size={16}
                className={`ml-auto shrink-0 text-[#665090] transition-transform ${ratingsOpen ? "rotate-180" : ""}`}
              />
            </button>
            {ratingsOpen && summary.calificaciones.count > 0 && (
              <div className="flex max-h-[320px] flex-col gap-2 overflow-y-auto">
                {summary.calificaciones.items.map((c) => (
                  <div
                    key={`${c.edition}-${c.email}`}
                    className="rounded-lg bg-[#F5F1FF] px-3 py-2 text-[13px]"
                  >
                    <span className="font-semibold text-[#2D2049]">
                      {c.rating} — {RATING_LABELS[c.rating]}
                    </span>
                    <span className="text-gray-500">
                      {" "}
                      · {`${c.name} ${c.surname}`.trim() || c.email} · {editionLabel(c.edition)}
                    </span>
                    {c.comment && (
                      <p className="m-0 mt-1 whitespace-pre-line text-[#4A3D73]">
                        {c.comment}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <FormacionDevolucionPanel formacionKey={summary.key} />
        </>
      ) : null}
    </div>
  );
};

export default FormacionInscriptosPanel;
